import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { BASE_PERMISSION, type PermissionAction } from '@/lib/constants/modules';
import type { PermissionMap, PermissionNode } from '@/types/api';
import { aclChildKey } from './acl-labels';

interface PermissionState {
  /** ACL permission tree for sub-super-admins. */
  perms: PermissionMap;
  /** moduleListingData — keyed by Manage X label, value is an allow-list of IDs. */
  moduleListing: Record<string, unknown>;
  /**
   * Reseller / end-user only: the ACL of the nearest sub-superadmin above
   * them. null = no sub-superadmin in the chain → no restriction.
   */
  inheritedPerms: PermissionMap | null;

  setPerms: (perms: PermissionMap) => void;
  setModuleListing: (data: Record<string, unknown>) => void;
  setInheritedPerms: (perms: PermissionMap | null) => void;
  reset: () => void;
}

/**
 * Persists into localStorage under "obd3:perms" and "obd3:moduleListing".
 * Note: legacy keys ("perms", "module_listing") are NOT re-used to keep
 * the namespaces clean — the AuthStore's clearSession() wipes legacy state
 * anyway on logout.
 */
export const usePermissionStore = create<PermissionState>()(
  persist(
    (set) => ({
      perms: {},
      moduleListing: {},
      inheritedPerms: null,
      setPerms: (perms) => set({ perms }),
      setModuleListing: (data) => set({ moduleListing: data }),
      setInheritedPerms: (perms) => set({ inheritedPerms: perms }),
      reset: () => set({ perms: {}, moduleListing: {}, inheritedPerms: null }),
    }),
    { name: 'obd3:permissions' },
  ),
);

/**
 * Walk the permission tree with a dot-delimited path.
 * Example: hasPermission(perms, "Manage Users.Create User", "add")
 *
 * Mirrors legacy Container.hasPermission semantics.
 */
export function hasPermission(
  perms: PermissionMap,
  modulePath: string,
  action: PermissionAction = BASE_PERMISSION.VIEW,
): boolean {
  if (!modulePath) return false;
  if (Object.keys(perms).length === 0) return false;

  const parts = modulePath.split('.');
  let node: PermissionNode | undefined;

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (!part) return false;
    if (i === 0) {
      node = perms[part];
    } else if (node?.children && !Array.isArray(node.children)) {
      // DB ka naam pehle; nahi mila to panel wale naam (jaise "Directory") se DB key ("User List") try karo.
      node = node.children[part] ?? node.children[aclChildKey(parts[0] ?? '', part)];
    } else {
      node = undefined;
    }
    if (!node) return false;
  }

  if (!node) return false;
  return Boolean(node[action]);
}
